import React, { useEffect, useState } from 'react';
import type { AppContext } from '../app';
import { getMascotManager } from '../moderation/MascotManager';
import type { MascotState } from '../moderation/MascotManager';
import { useShell } from './shells/ShellProvider';

interface Props {
  ctx: AppContext;
}

/**
 * MascotSigil — Phase 71 partial scaffold.
 *
 * Small visible sigil top-right under the SessionRecorder pill. Four
 * states drive the glyph + animation; tap summons the `sass-mascot`
 * drawer surface.
 *
 * The mascot has personality intentionally — its role is to make
 * personal moderation visible (not silent), so the user always knows
 * what got hidden + why.
 */
export function MascotSigil({ ctx }: Props): JSX.Element | null {
  const mascot = getMascotManager(ctx);
  const { shell } = useShell();
  const [state, setState] = useState<MascotState>(() => mascot.state());
  const [activeId, setActiveId] = useState<string | null>(() => ctx.director.state().active);

  useEffect(() => mascot.onState(setState), [mascot]);
  useEffect(() => ctx.director.subscribe((s) => setActiveId(s.active)), [ctx]);

  // Auto-hide on the drawer itself so the user can see what they came for.
  if (activeId === 'sass-mascot') return null;

  // Phase 73 full — if the active shell defines a mascot persona, its
  // expressions override the abstract sigil glyphs. Pip the fox sigil
  // for the Kids C shell, etc. Falls through to the abstract default.
  const personaGlyph =
    shell.mascot?.expressions?.[
      state.kind === 'just-flagged' ? 'justFlagged' :
      state.kind === 'watching'     ? 'watching' :
      state.kind === 'summoned'     ? 'summoned' :
                                      'idle'
    ] ?? shell.mascot?.glyph;

  const glyph = personaGlyph ?? (
    state.kind === 'just-flagged' ? '⚠' :
    state.kind === 'watching'     ? '◉' :
    state.kind === 'summoned'     ? '◎' :
                                    '◌'
  );

  const personaName = shell.mascot?.id;
  const title = personaName
    ? (state.kind === 'just-flagged' ? `${personaName}: ${state.category} flagged · tap to review` :
       state.kind === 'watching'     ? `${personaName}: watching` :
       state.kind === 'summoned'     ? `${personaName}: drawer open elsewhere` :
                                       `${personaName}: idle · tap to talk`)
    : (state.kind === 'just-flagged' ? `mascot: ${state.category} flagged · tap to review` :
       state.kind === 'watching'     ? 'mascot: watching' :
       state.kind === 'summoned'     ? 'mascot: drawer open elsewhere' :
                                       'mascot: idle · tap to configure');

  return (
    <button
      type="button"
      className={`mascot-sigil mascot-sigil-${state.kind}`}
      data-testid="mascot-sigil"
      data-state-kind={state.kind}
      aria-label={title}
      title={title}
      onClick={() => {
        mascot.summon();
        ctx.director.bridge('sass-mascot', 'mascot drawer');
      }}
    >
      <span aria-hidden className="mascot-sigil-glyph">{glyph}</span>
      <style>{sigilStyles}</style>
    </button>
  );
}

const sigilStyles = `
.mascot-sigil {
  position: fixed;
  top: 12px;
  right: 70px;
  z-index: 49;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: var(--bg-2);
  border: 1px solid var(--phos-dim);
  color: var(--phos);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--mono);
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
}
.mascot-sigil:hover { border-color: var(--phos); }
.mascot-sigil-just-flagged {
  border-color: var(--amber);
  color: var(--amber);
  animation: mascot-pulse 800ms ease-in-out infinite;
}
.mascot-sigil-watching { opacity: 0.85; }
.mascot-sigil-summoned { opacity: 0.6; }
.mascot-sigil-glyph { line-height: 1; }
@keyframes mascot-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.12); }
}
@media (max-width: 700px) {
  .mascot-sigil { top: 8px; right: 8px; width: 26px; height: 26px; font-size: 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .mascot-sigil-just-flagged { animation: none; }
}
`;
